import React, { useEffect, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { bdDashboardAPI } from "../../api";
import { chartFont } from "./wplDashboardHelpers";
import TurbineAnimation from "../../components/turbine-animation/TurbineAnimation";

const theme = {
  text: "#ffffff",
  muted: "#d3e3dd",
  grid: "#3f7468",
  tooltipBg: "#1b443e",
  border: "#3f7468",
  accentAlert: "#f5894a",
  accentBrand: "#38b97c",
};

const SERVICES = [
  "Turbine restart Recommendations",
  "Arbitrations",
  "Maintenance Strategies",
  "Inspection Strategy",
];

const formatCount = (value) =>
  value === null || value === undefined ? "—" : Number(value).toLocaleString();

// Styled hover tooltip; the native title attribute only appears after a ~1s
// hover delay, which reads as "no tooltip".
function InfoTip({ text }) {
  return (
    <span className="bd-info-tip">
      ⓘ
      <span className="bd-info-tip-bubble">{text}</span>
    </span>
  );
}

function DefectTypeChart({ data, fill }) {
  if (!data || data.length === 0) {
    return <p className="bd-empty">No data available.</p>;
  }
  const chartData = data.map((point) => ({
    label: String(point.x),
    value: Number(point.y) || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={chartData} margin={{ left: 0, right: 10 }}>
        <CartesianGrid stroke={theme.grid} vertical={false} />
        <XAxis
          dataKey="label"
          interval={0}
          angle={-35}
          textAnchor="end"
          height={70}
          tick={{ fill: theme.muted, fontSize: chartFont(13) }}
          axisLine={{ stroke: theme.grid }}
          tickLine={{ stroke: theme.grid }}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fill: theme.muted, fontSize: chartFont(13) }}
          axisLine={{ stroke: theme.grid }}
          tickLine={{ stroke: theme.grid }}
        />
        <Tooltip
          cursor={{ fill: "rgba(255, 255, 255, 0.08)" }}
          formatter={(value) => [value, "Defects"]}
          contentStyle={{
            background: theme.tooltipBg,
            border: `1px solid ${theme.border}`,
            color: theme.text,
          }}
          itemStyle={{ color: theme.text }}
          labelStyle={{ color: theme.muted }}
        />
        <Bar
          isAnimationActive={false}
          dataKey="value"
          fill={fill}
          radius={[3, 3, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function WplGlobalViewSection({ onLoaded, screensaverWaiting }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    let isMounted = true;
    const fetchGlobalView = () =>
      bdDashboardAPI
        .globalView()
        .then((payload) => {
          if (!isMounted) return;
          // Temporary: which table/rows decide the "Data since" year.
          // eslint-disable-next-line no-console
          console.log(
            "[WPL] Data since",
            payload.earliest_year,
            payload.earliest_year_sources
          );
          setData(payload);
          setError(null);
        })
        .catch((err) => {
          if (!isMounted) return;
          setError(err);
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });

    fetchGlobalView();
    const intervalId = setInterval(fetchGlobalView, 10 * 60 * 1000);
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  // Report readiness whenever data is loaded — including when the screensaver
  // starts waiting on a section that already finished loading earlier.
  useEffect(() => {
    if (!loading) onLoadedRef.current?.();
  }, [loading, screensaverWaiting]);

  if (loading) {
    return <TurbineAnimation overlayClassname="bd-windfarms-loading" />;
  }

  if (error || !data) {
    return (
      <p className="bd-error">Could not load the global view. Please try again.</p>
    );
  }

  return (
    <section className="bd-section">
      <h2>
        {data.earliest_year
          ? `Global View — Data since ${data.earliest_year}`
          : "Global View"}
      </h2>
      <div className="bd-global-grid">
        <div className="bd-chart-card bd-global-card">
          <div className="bd-chart-header">
            <h3>Clients Supported Worldwide</h3>
            <p>Owners – Operators – Asset Managers – Loss Adjusters</p>
          </div>
          <div className="bd-global-stat">
            <span className="bd-global-stat-label">
              Clients Supported{" "}
              <InfoTip text="Distinct clients across all My Hours projects, all time" />
            </span>
            <span className="bd-global-stat-value">
              {formatCount(data.clients_supported)}
            </span>
          </div>
          <div className="bd-global-stat">
            <span className="bd-global-stat-label">
              Blade Repair Recommendations{" "}
              <InfoTip text="Repair recommendations issued on blade inspection reports, all time" />
            </span>
            <span className="bd-global-stat-value">
              {formatCount(data.blade_repair_recommendations)}
            </span>
          </div>
          <div className="bd-global-stat">
            <span className="bd-global-stat-label">
              Root Cause Analysis (RCA) Projects{" "}
              <InfoTip text="All RCA projects on the Monday boards, all time" />
            </span>
            <span className="bd-global-stat-value">
              {formatCount(data.rca_projects)}
            </span>
          </div>
          <ul className="bd-global-services">
            {SERVICES.map((service) => (
              <li key={service}>{service}</li>
            ))}
          </ul>
        </div>

        <div className="bd-chart-card bd-global-card">
          <div className="bd-chart-header">
            <h3>Identified Internal Defects</h3>
            <p>Across various blade inspections</p>
          </div>
          <div className="bd-global-stat">
            <span className="bd-global-stat-label">
              Defects identified{" "}
              <InfoTip text="Internal defects recorded across blade inspections, all time" />
            </span>
            <span className="bd-global-stat-value">
              {formatCount(data.internal_defects?.total)}
            </span>
          </div>
          <DefectTypeChart
            data={data.internal_defects?.by_type}
            fill={theme.accentBrand}
          />
        </div>

        <div className="bd-chart-card bd-global-card">
          <div className="bd-chart-header">
            <h3>Identified External Defects</h3>
            <p>Across various blade inspections</p>
          </div>
          <div className="bd-global-stat">
            <span className="bd-global-stat-label">
              Defects identified{" "}
              <InfoTip text="External defects recorded across blade inspections, all time" />
            </span>
            <span className="bd-global-stat-value">
              {formatCount(data.external_defects?.total)}
            </span>
          </div>
          <DefectTypeChart
            data={data.external_defects?.by_type}
            fill={theme.accentAlert}
          />
        </div>

        <div className="bd-chart-card bd-global-card">
          <div className="bd-chart-header">
            <h3>OEM Blades Analysed</h3>
            <p>Major OEMs covered</p>
          </div>
          {data.oem_blades?.length > 0 ? (
            <div className="bd-global-oem-grid">
              {data.oem_blades.map((oem) => (
                <span key={oem} className="bd-global-oem">
                  {oem}
                </span>
              ))}
            </div>
          ) : (
            <p className="bd-empty">No OEM data available.</p>
          )}
        </div>
      </div>
    </section>
  );
}
